<template>
    <div class="blackjack-seat-element">
        <div class="element-user">
            <button v-if="player === undefined" v-on:click="$parent.blackjackJoinButton(seat)" class="button-join">TAKE<br /> SEAT</button>
            <div v-else class="user-info">
                <div class="user-avatar">
                    <AvatarImage v-bind:image="player.user.avatar" />
                </div>
                <span v-html="player.user.username"></span>
            </div>
        </div>
        <div class="element-game" v-bind:class="{ 'game-completed': table.game.state === 'completed' }">
            <div v-if="['running', 'completed'].includes(table.game.state) === true && player !== undefined" class="game-stack">

                <div v-if="player.bet.actions.includes('split') === false" class="stack-single">
                    <transition-group name="player" tag="div" class="single-cards">
                        <BlackjackCard v-for="(card, index) of player.bet.cards" v-bind:key="index + card.suit + card.rank" v-bind:card="card" />
                    </transition-group>
                    <BlackjackValue 
                        v-bind:value="blackjackGetCardValue(player.bet.cards)" 
                        v-bind:state="blackjackGetCardState(player.bet.cards)"  
                        v-bind:class="{
                            'value-active': blackjackIsActive,
                            'value-blackjack': player.bet.cards.length === 2 && blackjackGetCardValue(player.bet.cards) === 21 
                        }" 
                    />
                </div>
                <div v-else class="stack-split">
                    <div class="split-left">
                        <transition-group name="player" tag="div" class="left-cards">
                            <BlackjackCard v-for="(card, index) of player.bet.cardsLeft" v-bind:key="index + card.suit + card.rank" v-bind:card="card" />
                        </transition-group>
                        <BlackjackValue 
                            v-bind:value="blackjackGetCardValue(player.bet.cardsLeft)" 
                            v-bind:state="blackjackGetCardState(player.bet.cardsLeft)" 
                            v-bind:class="{ 
                                'value-active': blackjackIsActive && (player.bet.actions.includes('stand') === true || blackjackGetCardValue(player.bet.cardsRight) >= 21),
                                'value-blackjack': player.bet.cardsLeft.length === 2 && blackjackGetCardValue(player.bet.cardsLeft) === 21  
                            }" 
                        />
                    </div>
                    <div class="split-right">
                        <transition-group name="player" tag="div" class="right-cards">
                            <BlackjackCard v-for="(card, index) of player.bet.cardsRight" v-bind:key="index + card.suit + card.rank" v-bind:card="card" />
                        </transition-group>
                        <BlackjackValue 
                            v-bind:value="blackjackGetCardValue(player.bet.cardsRight)" 
                            v-bind:state="blackjackGetCardState(player.bet.cardsRight)"
                            v-bind:class="{ 
                                'value-active': blackjackIsActive && player.bet.actions.includes('stand') === false && blackjackGetCardValue(player.bet.cardsRight) < 21,
                                'value-blackjack': player.bet.cardsRight.length === 2 && blackjackGetCardValue(player.bet.cardsRight) === 21  
                            }" 
                        />
                    </div>
                </div>

            </div>
            <div class="game-actions">
                <button v-on:click="$parent.blackjackBetButton(seat, 'sideLeft')" class="button-side">
                    <transition name="slide-fade" mode="in-out">
                        <div v-if="blackjackGetAmount.sideLeft >= chipThresholds.orange" class="button-chip" key="orange-chip">
                            <img src="@/assets/img/blackjack/chip-orange.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideLeft)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideLeft >= chipThresholds.blue" class="button-chip" key="blue-chip">
                            <img src="@/assets/img/blackjack/chip-blue.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideLeft)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideLeft >= chipThresholds.purple" class="button-chip" key="purple-chip">
                            <img src="@/assets/img/blackjack/chip-purple.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideLeft)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideLeft >= chipThresholds.red" class="button-chip" key="red-chip">
                            <img src="@/assets/img/blackjack/chip-red.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideLeft)}}</div>
                        </div>
                    </transition>
                </button>
                <button v-on:click="$parent.blackjackBetButton(player.seat, 'main')" class="button-main">
                    <transition name="slide-fade" mode="in-out">
                        <div v-if="blackjackGetAmount.main >= chipThresholds.orange" class="button-chip" key="orange-chip">
                            <img src="@/assets/img/blackjack/chip-orange.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.main)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.main >= chipThresholds.blue" class="button-chip" key="blue-chip">
                            <img src="@/assets/img/blackjack/chip-blue.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.main)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.main >= chipThresholds.purple" class="button-chip" key="purple-chip">
                            <img src="@/assets/img/blackjack/chip-purple.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.main)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.main >= chipThresholds.red" class="button-chip" key="red-chip">
                            <img src="@/assets/img/blackjack/chip-red.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.main)}}</div>
                        </div>
                    </transition>
                </button>
                <button v-on:click="$parent.blackjackBetButton(seat, 'sideRight')" class="button-side">
                    <transition name="slide-fade" mode="in-out">
                        <div v-if="blackjackGetAmount.sideRight >= chipThresholds.orange" class="button-chip" key="orange-chip">
                            <img src="@/assets/img/blackjack/chip-orange.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideRight)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideRight >= chipThresholds.blue" class="button-chip" key="blue-chip">
                            <img src="@/assets/img/blackjack/chip-blue.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideRight)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideRight >= chipThresholds.purple" class="button-chip" key="purple-chip">
                            <img src="@/assets/img/blackjack/chip-purple.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideRight)}}</div>
                        </div>
                        <div v-else-if="blackjackGetAmount.sideRight >= chipThresholds.red" class="button-chip" key="red-chip">
                            <img src="@/assets/img/blackjack/chip-red.png" />
                            <div class="chip-amount">{{blackjackFormatAmount(blackjackGetAmount.sideRight)}}</div>
                        </div>
                    </transition>
                </button>
            </div>
        </div>
        <div class="element-win">
            
        </div>
    </div>
</template>

<script>
    import AvatarImage from '@/components/AvatarImage';
    import BlackjackCard from '@/components/blackjack/BlackjackCard';
    import BlackjackValue from '@/components/blackjack/BlackjackValue';

    export default {
        name: 'BlackjackSeatElement',
        components: {
            AvatarImage,
            BlackjackCard,
            BlackjackValue
        },
        props: [
            'table',
            'seat', 
            'player'
        ],
        methods: {
            blackjackGetCardValue(cards) {
                let value = 0;
                let aces = false;

                for(const card of cards) {
                    if(card.rank === 'A') {
                        value = value + 1;
                        aces = true;
                    } else if(card.rank == 'K' || card.rank == 'Q' || card.rank == 'J') {
                        value = value + 10;
                    } else if(card.rank != 'hidden') {
                        value = value + Math.floor(card.rank);
                    }
                }

                if(aces == true && value <= 11) {
                    value = value + 10;
                }

                return value;
            },
            blackjackGetCardState(cards) {
                let state = '';

                if(this.table.game.state === 'completed' && (this.blackjackGetCardValue(cards) > this.blackjackGetCardValue(this.table.game.dealerCards) || this.blackjackGetCardValue(this.table.game.dealerCards) > 21) && this.blackjackGetCardValue(cards) <= 21) { state = 'won'; }
                else if(this.blackjackGetCardValue(cards) > 21 || (this.table.game.state === 'completed' && this.blackjackGetCardValue(cards) < this.blackjackGetCardValue(this.table.game.dealerCards) && this.blackjackGetCardValue(this.table.game.dealerCards) <= 21)) { state = 'lose'; }

                return state;
            },
            blackjackFormatAmount(amount) {
                if(amount >= 1000) { amount = parseFloat(amount / 1000) + 'K'; }
                return amount;
            }
        },
        computed: {
            chipThresholds() {
                return this.table.game.type === 'standard'
                    ? { orange: 10, blue: 5, purple: 2, red: 1 }
                    : { orange: 100, blue: 50, purple: 25, red: 10 };
            },
            blackjackIsActive() {
                return this.table.playersPos === this.seat;
            },
            blackjackGetAmount() {
                let amount = {
                    main: 0,
                    sideLeft: 0,
                    sideRight: 0
                };

                if(this.player !== undefined && this.player.bet !== null) {
                    amount.main = this.player.bet.amount.main;
                    amount.sideLeft = this.player.bet.amount.sideLeft;
                    amount.sideRight = this.player.bet.amount.sideRight;
                }

                return amount;
            }
        }
    }
</script>

<style scoped>
    .blackjack-seat-element {
        position: absolute;
    }

    .blackjack-seat-element:nth-child(1) {
        top: 48%;
        left: -20px;
    }

    .blackjack-seat-element:nth-child(2) {
        top: 82%;
        left: 150px;
    }

    .blackjack-seat-element:nth-child(3) {
        top: 92%;
        left: 50%;
        transform: translate(-50%, 0);
    }

    .blackjack-seat-element:nth-child(4) {
        top: 82%;
        right: 150px;
    }

    .blackjack-seat-element:nth-child(5) {
        top: 48%;
        right: -20px;
    }

    .blackjack-seat-element .element-user {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .blackjack-seat-element .element-user button.button-join {
        width: 98px;
        height: 98px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        font-size: 15px;
        font-weight: 800;
        color: var(--accent-btn-txt-color);
        background: var(--gradient-button-bg);
        border: 1px dashed var(--accent-yellow);
        cursor: pointer;
        transition: all 0.3s ease;
        word-break: break-word;
        text-align: center;
        line-height: 1.2;
        padding: 5px;
        box-sizing: border-box;
    }

    .blackjack-seat-element .element-user button.button-join:hover {
        transform: scale(1.05);
        background: var(--gradient-yellow);
        box-shadow: 0 0 8px var(--accent-yellow), 0 0 12px var(--accent-yellow-main);
    }

    .blackjack-seat-element .element-user button.button-join:active {
        transform: scale(0.95);
    }

    .blackjack-seat-element .user-info{
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .blackjack-seat-element .user-info span {
        margin-top: 10px;
        font-size: 15px;
        font-weight: 700;
        color: #ffffff;
    }

    .blackjack-seat-element .user-avatar {
        width: 98px;
        height: 98px;
        display: flex;
        justify-content: center;
        align-items: flex-end;
        border-radius: 50%;
        background-color: #000f1b;
        border: 2px solid #9e9e9e;
        overflow: hidden;
    }

    .blackjack-seat-element .user-avatar .avatar-image {
        width: 77px;
        height: 77px;
    }

    .blackjack-seat-element .element-game {
        position: absolute;
    }

    .blackjack-seat-element:nth-child(1) .element-game {
        top: -15px;
        right: -110px;
        transform: rotate(59deg);
    }

    .blackjack-seat-element:nth-child(2) .element-game {
        top: -75px;
        left: 85px;
        transform: rotate(30deg);
    }

    .blackjack-seat-element:nth-child(3) .element-game {
        top: -105px;
    }

    .blackjack-seat-element:nth-child(4) .element-game {
        top: -75px;
        right: 85px;
        transform: rotate(-30deg);
    }

    .blackjack-seat-element:nth-child(5) .element-game {
        top: -15px;
        left: -110px;
        transform: rotate(-59deg);
    }

    .blackjack-seat-element .game-stack {
        position: absolute;
        bottom: 80px;
        left: 50%;
        transform: translate(-50%, 0);
    }

    .blackjack-seat-element .stack-single {

    }

    .blackjack-seat-element .stack-split {
        position: relative;
    }

    .blackjack-seat-element .split-left {
        position: absolute;
        bottom: 0;
        left: -95px;
    }

    .blackjack-seat-element .split-right {
        position: absolute;
        bottom: 0;
        right: -70px;
    }

    .blackjack-seat-element .single-cards {
        position: relative;
        width: 80px;
    }


    .blackjack-seat-element .left-cards,
    .blackjack-seat-element .right-cards {
        position: relative;
        width: 73px;
    }

    .blackjack-seat-element .player-enter-active {
        transition: all 0.6s;
    }

    .blackjack-seat-element .player-enter {
        transform: translate(5px, -25px);
        opacity: 0;
    }

    .blackjack-seat-element .game-actions {
        width: 100px;
        height: 60px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-image: url('~@/assets/img/blackjack-bet.png');
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        position: relative;
    }

    .blackjack-seat-element .game-actions::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--gradient-yellow);
        opacity: 0.15;
        mix-blend-mode: color;
        pointer-events: none;
        border-radius: 8px;
    }

    .blackjack-seat-element .game-actions button.button-main {
        width: 60px;
        height: 60px;
        position: relative;
        border-radius: 50%;
    }

    .blackjack-seat-element .game-actions button.button-side {
        width: 20px;
        height: 45px;
        position: relative;
        background: transparent;
        border: none;
        cursor: pointer;
    }

    .blackjack-seat-element .game-actions button.button-side::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--gradient-button-bg);
        opacity: 0.1;
        border-radius: 4px;
        transition: all 0.3s ease;
        pointer-events: none;
    }

    .blackjack-seat-element .game-actions button.button-side:hover::before {
        opacity: 0.3;
        background: var(--gradient-yellow);
        box-shadow: 0 0 6px var(--accent-yellow);
    }

    .blackjack-seat-element .game-actions button.button-main .button-chip {
        width: 50px;
        height: 50px;
        position: absolute;
        top: 5px;
        left: 5px;
    }

    .blackjack-seat-element .game-actions button.button-side .button-chip {
        width: 27px;
        height: 27px;
        position: absolute;
        top: 9px;
    }

    .blackjack-seat-element .game-actions button.button-side:first-of-type .button-chip {
        left: -12.5px;
    }

    .blackjack-seat-element .game-actions button.button-side:last-of-type .button-chip {
        right: -12.5px;
    }

    .blackjack-seat-element .game-actions button.button-main .slide-fade-enter-active,
    .blackjack-seat-element .game-actions button.button-side .slide-fade-enter-active {
        transition: all 0.5s ease;
    }

    .blackjack-seat-element .game-actions button.button-main .slide-fade-enter,
    .blackjack-seat-element .game-actions button.button-side .slide-fade-enter {
        transform: translateY(-20px);
        opacity: 0;
    }

    .blackjack-seat-element .game-actions button.button-main .button-chip img,
    .blackjack-seat-element .game-actions button.button-side .button-chip img {
        width: 100%;
        height: 100%;
    }

    .blackjack-seat-element .game-actions button.button-main .chip-amount,
    .blackjack-seat-element .game-actions button.button-side .chip-amount {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
        font-weight: 800;
        color: #ffffff;
    }

    .blackjack-seat-element .game-actions button.button-side .chip-amount {
        font-size: 8px;
    }

    @media only screen and (max-width: 1200px) {

        .blackjack-seat-element:nth-child(1) {
            left: 10px;
        }

        .blackjack-seat-element:nth-child(5) {
            right: 10px;
        }

    }

    @media only screen and (max-width: 768px) {

        .blackjack-seat-element:nth-child(1) {
            top: 45%;
            left: -10px;
        }

        .blackjack-seat-element:nth-child(2) {
            top: 78%;
            left: 60px;
        }

        .blackjack-seat-element:nth-child(3) {
            top: 88%;
        }

        .blackjack-seat-element:nth-child(4) {
            top: 78%;
            right: 60px;
        }

        .blackjack-seat-element:nth-child(5) {
            top: 45%;
            right: -10px;
        }

        .blackjack-seat-element .element-user button.button-join {
            width: 70px;
            height: 70px;
            font-size: 10px;
            line-height: 1.1;
            padding: 4px;
        }

        .blackjack-seat-element .user-avatar {
            width: 70px;
            height: 70px;
        }

        .blackjack-seat-element .user-avatar .avatar-image {
            width: 55px;
            height: 55px;
        }

        .blackjack-seat-element .user-info span {
            font-size: 12px;
        }

        .blackjack-seat-element:nth-child(1) .element-game {
            right: -80px;
        }

        .blackjack-seat-element:nth-child(2) .element-game {
            top: -60px;
            left: 60px;
        }

        .blackjack-seat-element:nth-child(3) .element-game {
            top: -80px;
        }

        .blackjack-seat-element:nth-child(4) .element-game {
            top: -60px;
            right: 60px;
        }

        .blackjack-seat-element:nth-child(5) .element-game {
            left: -80px;
        }

        .blackjack-seat-element .game-actions {
            width: 80px;
            height: 48px;
        }

        .blackjack-seat-element .game-actions button.button-main {
            width: 48px;
            height: 48px;
        }

        .blackjack-seat-element .game-actions button.button-main .button-chip {
            width: 40px;
            height: 40px;
            top: 4px;
            left: 4px;
        }

        .blackjack-seat-element .game-actions button.button-side .button-chip {
            width: 22px;
            height: 22px;
            top: 7px;
        }

        .blackjack-seat-element .game-actions button.button-main .chip-amount {
            font-size: 10px;
        }

        .blackjack-seat-element .game-actions button.button-side .chip-amount {
            font-size: 7px;
        }

        .blackjack-seat-element .single-cards {
            width: 60px;
        }

        .blackjack-seat-element .left-cards,
        .blackjack-seat-element .right-cards {
            width: 55px;
        }

        .blackjack-seat-element .game-stack {
            bottom: 60px;
        }

        .blackjack-seat-element .split-left {
            left: -70px;
        }

        .blackjack-seat-element .split-right {
            right: -55px;
        }

    }

    @media only screen and (max-width: 480px) {

        .blackjack-seat-element:nth-child(1) {
            top: 42%;
            left: -20px;
        }

        .blackjack-seat-element:nth-child(2) {
            top: 75%;
            left: 30px;
        }

        .blackjack-seat-element:nth-child(3) {
            top: 85%;
        }

        .blackjack-seat-element:nth-child(4) {
            top: 75%;
            right: 30px;
        }

        .blackjack-seat-element:nth-child(5) {
            top: 42%;
            right: -20px;
        }

        .blackjack-seat-element .element-user button.button-join {
            width: 55px;
            height: 55px;
            font-size: 8px;
            line-height: 1.1;
            padding: 3px;
        }

        .blackjack-seat-element .user-avatar {
            width: 55px;
            height: 55px;
        }

        .blackjack-seat-element .user-avatar .avatar-image {
            width: 43px;
            height: 43px;
        }

        .blackjack-seat-element .user-info span {
            font-size: 10px;
            margin-top: 5px;
        }

        .blackjack-seat-element:nth-child(1) .element-game {
            right: -60px;
        }

        .blackjack-seat-element:nth-child(2) .element-game {
            top: -50px;
            left: 45px;
        }

        .blackjack-seat-element:nth-child(3) .element-game {
            top: -65px;
        }

        .blackjack-seat-element:nth-child(4) .element-game {
            top: -50px;
            right: 45px;
        }

        .blackjack-seat-element:nth-child(5) .element-game {
            left: -60px;
        }

        .blackjack-seat-element .game-actions {
            width: 65px;
            height: 40px;
        }

        .blackjack-seat-element .game-actions button.button-main {
            width: 40px;
            height: 40px;
        }

        .blackjack-seat-element .game-actions button.button-main .button-chip {
            width: 32px;
            height: 32px;
            top: 4px;
            left: 4px;
        }

        .blackjack-seat-element .game-actions button.button-side .button-chip {
            width: 18px;
            height: 18px;
            top: 6px;
        }

        .blackjack-seat-element .game-actions button.button-main .chip-amount {
            font-size: 8px;
        }

        .blackjack-seat-element .game-actions button.button-side .chip-amount {
            font-size: 6px;
        }

        .blackjack-seat-element .single-cards {
            width: 48px;
        }

        .blackjack-seat-element .left-cards,
        .blackjack-seat-element .right-cards {
            width: 45px;
        }

        .blackjack-seat-element .game-stack {
            bottom: 50px;
        }

        .blackjack-seat-element .split-left {
            left: -60px;
        }

        .blackjack-seat-element .split-right {
            right: -45px;
        }

    }
</style>
